/**
 * The words and badges the document screens agree on.
 *
 * One place per enum, because the same status has to read the same way in the
 * header, in the review-due list, and in a search result — and because a status
 * rendered with the wrong tone is an argument the UI makes without meaning to.
 * The two that carry real weight:
 *
 * - **`needs_review` is a warning, never an error and never dimmed.** §7.2:
 *   "unpublishing stale documents is worse than stale documents". A stale
 *   playbook is still the team's answer; the tone says "read me again", not
 *   "do not trust this".
 * - **`in_review` is reachable as of 1b.** A document enters it by having an
 *   approval request opened on it (§7.6) and leaves it when the request is
 *   rejected or expired, or when the approved draft is published. Nothing in the
 *   SPA sets the status — it is a status to render, never one to send.
 */

import type { ContentSource, DocumentStatus, DocumentType, SpaceKind } from '../../api/types.ts';
import { CLIENT_FACING_DOCUMENT_TYPES } from '../../api/types.ts';
import { Badge, type BadgeTone } from '../../components/ui/badge.tsx';

const TYPE_LABELS: Record<DocumentType, string> = {
  playbook: 'Playbook',
  reference: 'Reference',
  project_note: 'Project note',
  status_update: 'Status update',
  deliverable: 'Deliverable',
};

/** §7.1's own column, so the picker explains the type rather than naming it. */
const TYPE_DESCRIPTIONS: Record<DocumentType, string> = {
  playbook: 'SOPs and process docs. Internal, and on a review cycle.',
  reference: 'Environments, conventions, client quirks. Internal, and on a review cycle.',
  project_note: 'Meeting notes and decisions. Internal, timestamped, no review cycle.',
  status_update: 'Project-scoped and dated. Can be published into a client space.',
  deliverable: 'Written for an external audience, scoped to a project.',
};

export function documentTypeLabel(type: DocumentType): string {
  return TYPE_LABELS[type] ?? type;
}

export function documentTypeDescription(type: DocumentType): string {
  return TYPE_DESCRIPTIONS[type] ?? '';
}

export function isClientFacing(type: DocumentType): boolean {
  return CLIENT_FACING_DOCUMENT_TYPES.includes(type);
}

/**
 * The type, marked with whether it can leave the internal space.
 *
 * §7.1 — client-facing types are valid in both kinds and that is load-bearing:
 * a `status_update` is drafted internally and *becomes* client-facing by moving.
 * The rest publish in place. Saying which is which on the badge means the
 * publish dialog is not the first place someone learns it.
 */
export function DocumentTypeBadge({ type }: { type: DocumentType }) {
  return <Badge tone={isClientFacing(type) ? 'brand' : 'outline'}>{documentTypeLabel(type)}</Badge>;
}

const STATUS_LABELS: Record<DocumentStatus, string> = {
  draft: 'Draft',
  in_review: 'In review',
  published: 'Published',
  needs_review: 'Needs review',
  archived: 'Archived',
};

const STATUS_TONES: Record<DocumentStatus, BadgeTone> = {
  draft: 'gray',
  in_review: 'outline',
  published: 'success',
  // Warning, not error. See the note at the top of this file.
  needs_review: 'warning',
  archived: 'outline',
};

const STATUS_DESCRIPTIONS: Record<DocumentStatus, string> = {
  draft: 'Never published. Only people who can edit it can read it.',
  in_review:
    'With its reviewers (§7.6). Only people who can edit it can read it — it has never been published.',
  published: 'Readers resolve to the published version.',
  needs_review: 'Past its review date, and still fully readable by everyone who could read it.',
  archived: 'Out of circulation: excluded from search unless asked for. A direct link still works.',
};

export function documentStatusLabel(status: DocumentStatus): string {
  return STATUS_LABELS[status] ?? status;
}

export function DocumentStatusBadge({ status }: { status: DocumentStatus }) {
  return (
    <Badge tone={STATUS_TONES[status]}>
      <span title={STATUS_DESCRIPTIONS[status]}>{documentStatusLabel(status)}</span>
    </Badge>
  );
}

export function documentStatusDescription(status: DocumentStatus): string {
  return STATUS_DESCRIPTIONS[status] ?? '';
}

/**
 * Which of the three bodies §7.4 allows is on screen.
 *
 * The API decides this and reports it; nothing here re-derives it from the
 * document's status or from who is looking. A screen that assumed it was showing
 * the draft would show a reader stale-looking content and offer to edit it.
 */
export function ContentSourceBadge({
  source,
  versionNumber,
}: {
  source: ContentSource;
  versionNumber: number | null;
}) {
  if (source === 'draft') {
    return (
      <Badge tone="outline">
        <span title="Editors with access see the working draft. Readers do not (§7.4).">
          Working draft
        </span>
      </Badge>
    );
  }
  if (source === 'published') {
    return (
      <Badge tone="success">
        <span title="What readers resolve to (§7.4).">
          Published{versionNumber === null ? '' : ` · v${versionNumber}`}
        </span>
      </Badge>
    );
  }
  return <Badge tone="gray">Viewing v{versionNumber ?? '?'}</Badge>;
}

/** §4.3 — a space's kind is the single answer to who can see what is inside it. */
export function SpaceKindBadge({ kind }: { kind: SpaceKind }) {
  return (
    <Badge tone={kind === 'client' ? 'brand' : 'outline'}>
      {kind === 'client' ? 'Client space' : 'Internal space'}
    </Badge>
  );
}
